import React from 'react';
import { TaxRegionKey } from '@capintel/instruments-lib';
import { TaxCurrencyInput, TaxPercentageInput } from './components';
import { useTaxAnalysisInputs, FIELD_KEYS, FIELD_LABEL_KEYS } from '../../hooks';
import { formatMessage } from '@/utils/i18n';
import { PartialUsComparisonTaxSettings } from '@/types/tax-analysis';

const US_FIELD_KEYS = FIELD_KEYS[TaxRegionKey.US];
const US_FIELD_LABEL_KEYS = FIELD_LABEL_KEYS[TaxRegionKey.US];

interface TaxAnalysisInputsContainerProps {
  readOnly?: boolean;
  taxInputs: ReturnType<typeof useTaxAnalysisInputs>;
}

const INPUT_TYPE = {
  CURRENCY: 'currency',
  PERCENTAGE: 'percentage',
} as const;

type InputType = (typeof INPUT_TYPE)[keyof typeof INPUT_TYPE];

interface InputConfig {
  key: keyof PartialUsComparisonTaxSettings;
  type: InputType;
}

const INPUT_CONFIGS: InputConfig[] = [
  { key: US_FIELD_KEYS.SHORT_TERM_YTD_GAIN, type: INPUT_TYPE.CURRENCY },
  { key: US_FIELD_KEYS.SHORT_TERM_YTD_LOSS, type: INPUT_TYPE.CURRENCY },
  { key: US_FIELD_KEYS.LONG_TERM_YTD_GAIN, type: INPUT_TYPE.CURRENCY },
  { key: US_FIELD_KEYS.LONG_TERM_YTD_LOSS, type: INPUT_TYPE.CURRENCY },
  { key: US_FIELD_KEYS.SHORT_TERM_CARRIED_FORWARD_LOSS, type: INPUT_TYPE.CURRENCY },
  { key: US_FIELD_KEYS.LONG_TERM_CARRIED_FORWARD_LOSS, type: INPUT_TYPE.CURRENCY },
  { key: US_FIELD_KEYS.SHORT_TERM_CAPITAL_GAINS_RATE, type: INPUT_TYPE.PERCENTAGE },
  { key: US_FIELD_KEYS.LONG_TERM_CAPITAL_GAINS_RATE, type: INPUT_TYPE.PERCENTAGE },
];

// This component will be for US only. We will need a similar component for CA where we will do similar typing but with CA-specific fields
const TaxAnalysisInputsContainer: React.FC<TaxAnalysisInputsContainerProps> = ({ readOnly = false, taxInputs }) => {
  const {
    regionInputs,
    setShortTermYTDGain,
    setShortTermYTDLoss,
    setLongTermYTDGain,
    setLongTermYTDLoss,
    setShortTermCarriedForwardLoss,
    setLongTermCarriedForwardLoss,
    setShortTermCapitalGainsRate,
    setLongTermCapitalGainsRate,
  } = taxInputs;

  const setterMap: Record<keyof PartialUsComparisonTaxSettings, (value?: number) => void> = {
    [US_FIELD_KEYS.SHORT_TERM_YTD_GAIN]: setShortTermYTDGain,
    [US_FIELD_KEYS.SHORT_TERM_YTD_LOSS]: setShortTermYTDLoss,
    [US_FIELD_KEYS.LONG_TERM_YTD_GAIN]: setLongTermYTDGain,
    [US_FIELD_KEYS.LONG_TERM_YTD_LOSS]: setLongTermYTDLoss,
    [US_FIELD_KEYS.SHORT_TERM_CARRIED_FORWARD_LOSS]: setShortTermCarriedForwardLoss,
    [US_FIELD_KEYS.LONG_TERM_CARRIED_FORWARD_LOSS]: setLongTermCarriedForwardLoss,
    [US_FIELD_KEYS.SHORT_TERM_CAPITAL_GAINS_RATE]: setShortTermCapitalGainsRate,
    [US_FIELD_KEYS.LONG_TERM_CAPITAL_GAINS_RATE]: setLongTermCapitalGainsRate,
  };

  // Currently only US is supported
  const values = regionInputs?.values as PartialUsComparisonTaxSettings;

  return (
    <div className="tax-inputs-container">
      {INPUT_CONFIGS.map(({ key, type }) => {
        const InputComponent = type === INPUT_TYPE.CURRENCY ? TaxCurrencyInput : TaxPercentageInput;
        return (
          <InputComponent
            key={key}
            id={key}
            label={formatMessage(US_FIELD_LABEL_KEYS[key])}
            value={values[key]}
            onChange={setterMap[key]}
            disabled={readOnly}
          />
        );
      })}
    </div>
  );
};

export default TaxAnalysisInputsContainer;
